const EMPTY = 'utils/memoize/noValue';
export const NO_VALUE = Symbol(EMPTY);

const compareArrays = <A extends unknown[], B extends unknown[]>(
    a: A,
    b: B,
): boolean => {
    const same = a.length === b.length;
    if (!same) {
        return false;
    }
    for (const [idx, v] of b.entries()) {
        if (!Object.is(v, a[idx])) {
            return false;
        }
    }
    return true;
};

/**
 * Memoizes a function based on its arguments and dependencies, where the
 * comparison algorithm is a shallow equality check based on `Object.is`[^1]
 *
 * [^1]: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */
export function memoize<
    Deps extends unknown[],
    GetDeps extends () => Deps,
    // biome-ignore lint/suspicious/noExplicitAny: `Args` is not inferred from `fn`, so `unknown[]` would reject functions with typed parameters
    Args extends any[],
    // biome-ignore lint/suspicious/noExplicitAny: calling `fn` must yield `ReturnType<T>`, which an `unknown` return constraint does not
    T extends (...args: Args) => any,
>(fn: T, getDeps: GetDeps): (...args: Parameters<T>) => ReturnType<T> {
    let prevDeps = getDeps();
    let prevArgs: unknown[] = [];
    let output: ReturnType<T> | typeof NO_VALUE = NO_VALUE;
    return (...args: Args) => {
        const deps = getDeps();
        const sameDeps = compareArrays(prevDeps, deps);
        const sameInputs = compareArrays(prevArgs, args);
        if (!sameDeps || !sameInputs || output === NO_VALUE) {
            if (!sameDeps) {
                prevDeps = deps;
            }
            if (!sameInputs) {
                prevArgs = args;
            }
            output = fn(...args);
            if (output === NO_VALUE) {
                throw new Error('Unexpected memoize function return value');
            }
            return output;
        }
        return output;
    };
}
